import React, {useCallback, useEffect, useMemo, useRef, useState} from 'react';
import {
    Avatar,
    Box,
    Button,
    CircularProgress,
    Stack,
    Typography,
} from '@mui/material';
import AccountTreeOutlinedIcon from '@mui/icons-material/AccountTreeOutlined';
import CheckCircleOutlineIcon from '@mui/icons-material/CheckCircleOutline';
import RemoveCircleOutlineIcon from '@mui/icons-material/RemoveCircleOutline';
import useChartAiBridgeStore from '../../../../stores/useChartAiBridgeStore';
import {isDisplayableEntityLabel} from '../../services/inlineComponents/decodeInlineComponentStream';
import {resolvePreviewEntityImage} from '../../utils/mutationPreviewEntityImage';
import {
    formatChartErrorForUser,
    isAnalystVisibleChartPlanSummary,
    isHiddenChartMutationMessage,
    isSoftArrowEndpointError,
    summarizeMarkupApplyResult,
} from '../../../whiteboards/ai/chartUserMessages';
import {parseChartMutationMarkup, isSelfReferentialArrow} from '../../../whiteboards/ai/parseChartMutationMarkup';

export const CHART_PREVIEW_EMPTY_COPY = 'No applicable chart changes';
export const CHART_BRIDGE_NOT_READY_COPY = 'Open chart to apply';

function isBridgeNotReadyMessage(error) {
    const raw = typeof error === 'string' ? error : error?.message || String(error || '');
    return /open chart to apply|chart editor is not available/i.test(raw);
}

/** Preview-row errors that should stay hidden — never the bridge-not-ready case. */
export function isHiddenPureLlmApplyError(error) {
    if (isBridgeNotReadyMessage(error)) {
        return false;
    }
    return isHiddenChartMutationMessage(error);
}

function previewApplyErrorText(errorMessage) {
    if (isBridgeNotReadyMessage(errorMessage)) {
        return CHART_BRIDGE_NOT_READY_COPY;
    }
    return formatChartErrorForUser(errorMessage) || 'This change could not be applied.';
}

const COMPACT_ACTION_SX = {
    minWidth: 0,
    px: 1,
    py: 0.25,
    fontSize: 11,
    textTransform: 'none',
};

/**
 * @param {Array<object>} ops
 * @param {Array<object>|null|undefined} [chartEntities]
 * @returns {Map<string, string>}
 */
export function buildMarkupLabelIndex(ops, chartEntities) {
    const index = new Map();
    for (const entity of chartEntities || []) {
        const ref = normalizeMarkupEntityRef(entity?.wikidataId || entity?.id || entity?.entityRef);
        const label = entity?.label;
        if (ref && isDisplayableEntityLabel(label, ref)) {
            index.set(ref, String(label).trim());
        }
    }
    for (const op of ops || []) {
        if ((op?.type === 'add-entity' || op?.type === 'create-local' || op?.type === 'remove-entity')
            && op.ref && op.label) {
            if (isDisplayableEntityLabel(op.label, op.ref)) {
                index.set(normalizeMarkupEntityRef(op.ref), String(op.label).trim());
            }
        }
    }
    return index;
}

function displayName(label, ref, fallback = 'entity') {
    if (isDisplayableEntityLabel(label, ref)) {
        return String(label).trim();
    }
    return fallback;
}

function displayRef(ref, labelIndex) {
    if (!ref) return 'entity';
    const normalized = normalizeMarkupEntityRef(ref);
    return displayName(labelIndex.get(normalized), ref, 'entity');
}

function formatConnectionLabel(from, to, relation) {
    if (relation) {
        return `${from} → ${to} (${relation})`;
    }
    return `${from} → ${to}`;
}

/**
 * Human-readable one-liner for a markup op.
 * @param {object} op
 * @param {Map<string, string>} [labelIndex]
 * @returns {string}
 */
export function describeMarkupOp(op, labelIndex = new Map()) {
    if (!op || typeof op !== 'object') {
        return 'Unknown change';
    }
    if (op.type === 'add-entity' || op.type === 'create-local') {
        return `Add ${displayName(op.label, op.ref)}`;
    }
    if (op.type === 'remove-entity') {
        return displayName(op.label, op.ref, displayRef(op.ref, labelIndex));
    }
    if (op.type === 'add-arrow') {
        const from = displayRef(op.from, labelIndex);
        const to = displayRef(op.to, labelIndex);
        return formatConnectionLabel(from, to, op.label);
    }
    if (op.type === 'remove-arrow') {
        const from = displayRef(op.from, labelIndex);
        const to = displayRef(op.to, labelIndex);
        return formatConnectionLabel(from, to, op.label);
    }
    if (op.type === 'update-arrow') {
        const from = displayRef(op.from, labelIndex);
        const to = displayRef(op.to, labelIndex);
        return formatConnectionLabel(from, to, op.label);
    }
    if (op.type === 'move-entity') {
        return displayName(op.label, op.ref);
    }
    return op.type || 'Unknown change';
}

function isArrowMarkupOp(op) {
    return op?.type === 'add-arrow' || op?.type === 'remove-arrow' || op?.type === 'update-arrow';
}

function markupOpActionVerb(op) {
    if (isRemoveMarkupOp(op)) {
        return 'Remove';
    }
    if (op?.type === 'add-arrow') {
        return 'Connect';
    }
    if (op?.type === 'update-arrow') {
        return 'Update';
    }
    return 'Add';
}

function markupOpAppliedLabel(op) {
    const verb = markupOpActionVerb(op);
    if (verb === 'Remove') {
        return 'Removed';
    }
    if (verb === 'Connect') {
        return 'Connected';
    }
    if (verb === 'Update') {
        return 'Updated';
    }
    return 'Added';
}

function findChartEntityImage(ref, chartEntities = [], op = null) {
    return resolvePreviewEntityImage(ref, chartEntities, op);
}

export function resolveMarkupOpImages(op, chartEntities = []) {
    if (!op || typeof op !== 'object') {
        return {imageUrl: '', fromUrl: '', toUrl: ''};
    }
    if (isArrowMarkupOp(op)) {
        return {
            imageUrl: '',
            fromUrl: findChartEntityImage(op.from, chartEntities, op),
            toUrl: findChartEntityImage(op.to, chartEntities, op),
        };
    }
    const ref = op.ref || op.from || op.to;
    return {
        imageUrl: findChartEntityImage(ref, chartEntities, op),
        fromUrl: '',
        toUrl: '',
    };
}

function avatarInitials(label) {
    const text = String(label || '').trim();
    if (!text) {
        return '?';
    }
    return text.slice(0, 1).toUpperCase();
}

function MarkupEntityAvatar({src, label, testId, size = 24}) {
    return (
        <Avatar
            alt=""
            src={src || undefined}
            data-testid={src ? undefined : testId}
            imgProps={src ? {'data-testid': testId} : undefined}
            sx={{width: size, height: size, fontSize: Math.round(size * 0.45)}}
        >
            {src ? null : avatarInitials(label)}
        </Avatar>
    );
}

/**
 * @param {object} op
 * @param {number} index
 * @returns {string}
 */
export function markupOpKey(op, index = 0) {
    if (!op || typeof op !== 'object') {
        return `op-${index}`;
    }
    if (op.type === 'add-entity' || op.type === 'create-local' || op.type === 'remove-entity') {
        return `${op.type}:${op.ref || op.label || index}`;
    }
    if (op.type === 'move-entity') {
        return `move-entity:${op.ref || index}:${op.x},${op.y}`;
    }
    if (op.type === 'add-arrow' || op.type === 'update-arrow' || op.type === 'remove-arrow') {
        // Stable without index — filtering earlier rows must not remap statuses.
        return `${op.type}:${op.from}->${op.to}:${op.label || ''}`;
    }
    return `${op.type || 'op'}-${index}`;
}

function isResolvedMarkupOpStatus(status) {
    return status === 'applied' || status === 'rejected' || status === 'error';
}

function keepResolvedMarkupOp(op, opStatuses) {
    return isResolvedMarkupOpStatus(opStatuses?.[markupOpKey(op)]);
}

function isRemoveMarkupOp(op) {
    return op?.type === 'remove-entity' || op?.type === 'remove-arrow';
}

function isEntityAddMarkupOp(op) {
    return op?.type === 'add-entity' || op?.type === 'create-local';
}

/**
 * Entities before arrows so leftover Connect can bind to a successful add
 * in the same auto-apply / Add-all batch.
 * @param {Array<{op: object, key: string, index?: number}>} entries
 * @returns {Array<{op: object, key: string, index?: number}>}
 */
function orderEntriesEntitiesFirst(entries) {
    const entityAdds = [];
    const rest = [];
    for (const entry of entries || []) {
        if (isEntityAddMarkupOp(entry?.op)) {
            entityAdds.push(entry);
        } else {
            rest.push(entry);
        }
    }
    return [...entityAdds, ...rest];
}

/**
 * Layout-only ops — never shown in the confirm list; bundled automatically when
 * content ops are applied (or auto-applied when markup is moves-only).
 * @param {object} op
 * @returns {boolean}
 */
export function isSilentMarkupOp(op) {
    return op?.type === 'move-entity';
}

/**
 * Ops that require Skip/Add confirmation in the preview.
 * @param {object} op
 * @returns {boolean}
 */
export function isConfirmableMarkupOp(op) {
    return Boolean(op && typeof op === 'object' && !isSilentMarkupOp(op));
}

/**
 * True when every parsed op is move-entity (tidy-only) — UI should auto-apply
 * like mutationMode=auto instead of showing an empty confirm preview.
 * @param {Array<object>|null|undefined} ops
 * @returns {boolean}
 */
export function isMovesOnlyMarkupOps(ops) {
    return Array.isArray(ops) && ops.length > 0 && ops.every(isSilentMarkupOp);
}

/**
 * @param {string|null|undefined} markup
 * @param {Array<object>|null|undefined} ops
 * @returns {boolean}
 */
export function isMovesOnlyMarkup(markup, ops) {
    const resolved = resolveMarkupPreviewInput({markup, ops});
    return isMovesOnlyMarkupOps(resolved.ops);
}

/**
 * True when markup/ops were proposed but every confirmable op is filtered out.
 * Moves-only markup is handled by the parent auto-apply path — not an empty preview.
 * @param {{
 *   markup?: string|null,
 *   ops?: Array<object>|null,
 *   chartEntities?: Array<object>|null,
 *   chartConnections?: Array<object>|null,
 * }} [input]
 * @returns {boolean}
 */
export function isEmptyConfirmableMarkupPreview({
    markup,
    ops,
    chartEntities,
    chartConnections,
    opStatuses,
    inferFromChart = false,
} = {}) {
    const hasInput = (typeof markup === 'string' && markup.trim().length > 0)
        || (Array.isArray(ops) && ops.length > 0);
    if (!hasInput || isMovesOnlyMarkup(markup, ops)) {
        return false;
    }
    const resolved = resolveMarkupPreviewInput({
        markup,
        ops,
        chartEntities,
        chartConnections,
        opStatuses,
        inferFromChart,
    });
    return !resolved.ops.some(isConfirmableMarkupOp);
}

/**
 * @param {string|null|undefined} ref
 * @returns {string|null|undefined}
 */
function normalizeMarkupEntityRef(ref) {
    if (ref == null) return ref;
    const trimmed = String(ref).trim();
    if (/^q\d+$/i.test(trimmed)) return trimmed.toUpperCase();
    return trimmed;
}

/** Refs only — layout x/y ticks must not retrigger markup parse / warning logs. */
function chartEntityIdentityKey(entities) {
    return (entities || [])
        .map((entity) => normalizeMarkupEntityRef(entity?.wikidataId || entity?.id || entity?.entityRef) || '')
        .filter(Boolean)
        .sort()
        .join('\0');
}

function chartConnectionIdentityKey(connections) {
    return (connections || [])
        .map((connection) => {
            const from = normalizeMarkupEntityRef(connection?.from);
            const to = normalizeMarkupEntityRef(connection?.to);
            if (!from || !to) return '';
            return `${from}->${to}:${connection?.label || ''}`;
        })
        .filter(Boolean)
        .sort()
        .join('\0');
}

function markupOpStatusesEqual(left, right) {
    if (left === right) {
        return true;
    }
    if (!left || !right) {
        return false;
    }
    const leftKeys = Object.keys(left);
    const rightKeys = Object.keys(right);
    if (leftKeys.length !== rightKeys.length) {
        return false;
    }
    return leftKeys.every((key) => left[key] === right[key]);
}

/**
 * Drop add-entity/create-local ops for refs already on the chart.
 * @param {Array<object>} ops
 * @param {Array<object>|null|undefined} chartEntities
 * @returns {Array<object>}
 */
export function filterAlreadyOnChartOps(ops, chartEntities, options = {}) {
    if (!Array.isArray(ops) || ops.length === 0) {
        return [];
    }
    const onChart = new Set();
    for (const entity of chartEntities || []) {
        const ref = normalizeMarkupEntityRef(entity?.wikidataId || entity?.id || entity?.entityRef);
        if (ref) {
            onChart.add(ref);
        }
    }
    if (onChart.size === 0) {
        return ops;
    }
    return ops.filter((op) => {
        if (keepResolvedMarkupOp(op, options.opStatuses)) {
            return true;
        }
        if (op?.type !== 'add-entity' && op?.type !== 'create-local') {
            return true;
        }
        const ref = normalizeMarkupEntityRef(op.ref);
        return !ref || !onChart.has(ref);
    });
}

function collectAcceptedAddRefs(ops, opStatuses) {
    const accepted = new Set();
    for (const op of ops || []) {
        if (op?.type !== 'add-entity' && op?.type !== 'create-local') {
            continue;
        }
        const ref = normalizeMarkupEntityRef(op.ref);
        if (!ref) {
            continue;
        }
        const status = opStatuses?.[markupOpKey(op)];
        // Failed / skipped adds are not real endpoints — leftover Connect must
        // not treat the pending markup ref as enough.
        if (status === 'error' || status === 'rejected') {
            continue;
        }
        accepted.add(ref);
    }
    return accepted;
}

function collectOnChartRefs(chartEntities) {
    const onChart = new Set();
    for (const entity of chartEntities || []) {
        const ref = normalizeMarkupEntityRef(entity?.wikidataId || entity?.id || entity?.entityRef);
        if (ref) {
            onChart.add(ref);
        }
    }
    return onChart;
}

function arrowEndpointKey(from, to) {
    const fromRef = normalizeMarkupEntityRef(from);
    const toRef = normalizeMarkupEntityRef(to);
    if (!fromRef || !toRef) {
        return '';
    }
    return `${fromRef}->${toRef}`;
}

function connectionExists(chartConnections, from, to) {
    const key = arrowEndpointKey(from, to);
    const reverse = arrowEndpointKey(to, from);
    if (!key) {
        return false;
    }
    return (chartConnections || []).some((connection) => {
        const existing = arrowEndpointKey(connection?.from, connection?.to);
        return existing === key || existing === reverse;
    });
}

function connectionHasLabel(chartConnections, from, to, label) {
    const key = arrowEndpointKey(from, to);
    const reverse = arrowEndpointKey(to, from);
    const wanted = label != null ? String(label).trim().toLowerCase() : '';
    if (!key || !wanted) {
        return false;
    }
    return (chartConnections || []).some((connection) => {
        const existing = arrowEndpointKey(connection?.from, connection?.to);
        if (existing !== key && existing !== reverse) {
            return false;
        }
        const existingLabel = connection?.label != null ? String(connection.label).trim().toLowerCase() : '';
        return existingLabel === wanted;
    });
}

/**
 * Infer `applied` from the live chart when persisted statuses are missing
 * (reload after Apply all). Never infers `rejected` — Skip cannot be seen on the chart.
 * @param {object} op
 * @param {Array<object>|null|undefined} chartEntities
 * @param {Array<object>|null|undefined} chartConnections
 * @returns {'applied'|null}
 */
export function inferMarkupOpStatusFromChart(op, chartEntities, chartConnections) {
    if (!op || typeof op !== 'object') {
        return null;
    }
    const onChart = collectOnChartRefs(chartEntities);
    if (op.type === 'add-entity' || op.type === 'create-local') {
        const ref = normalizeMarkupEntityRef(op.ref);
        return ref && onChart.has(ref) ? 'applied' : null;
    }
    if (op.type === 'remove-entity') {
        const ref = normalizeMarkupEntityRef(op.ref);
        if (!ref) {
            return null;
        }
        return onChart.has(ref) ? null : 'applied';
    }
    if (op.type === 'add-arrow') {
        return connectionExists(chartConnections, op.from, op.to) ? 'applied' : null;
    }
    if (op.type === 'remove-arrow') {
        return connectionExists(chartConnections, op.from, op.to) ? null : 'applied';
    }
    if (op.type === 'update-arrow') {
        return connectionHasLabel(chartConnections, op.from, op.to, op.label) ? 'applied' : null;
    }
    return null;
}

/**
 * @param {Array<object>} ops
 * @param {Array<object>|null|undefined} chartEntities
 * @param {Array<object>|null|undefined} chartConnections
 * @param {Record<string, string>|null|undefined} existing
 * @returns {Record<string, string>}
 */
export function inferMarkupOpStatusesFromChart(ops, chartEntities, chartConnections, existing = {}) {
    const next = existing && typeof existing === 'object' ? {...existing} : {};
    for (const op of ops || []) {
        const key = markupOpKey(op);
        if (next[key]) {
            continue;
        }
        const inferred = inferMarkupOpStatusFromChart(op, chartEntities, chartConnections);
        if (inferred) {
            next[key] = inferred;
        }
    }
    return next;
}

/**
 * Drop remove ops that the chart already reflects (entity/arrow gone).
 * Resolved rows stay so Apply/Skip still show Removed/Skipped.
 * @param {Array<object>} ops
 * @param {Array<object>|null|undefined} chartEntities
 * @param {Array<object>|null|undefined} chartConnections
 * @param {{opStatuses?: Record<string, string>|null}} [options]
 * @returns {Array<object>}
 */
export function filterAlreadyGoneRemoveOps(ops, chartEntities, chartConnections, options = {}) {
    if (!Array.isArray(ops) || ops.length === 0) {
        return [];
    }
    const onChart = collectOnChartRefs(chartEntities);
    return ops.filter((op) => {
        if (keepResolvedMarkupOp(op, options.opStatuses)) {
            return true;
        }
        if (op?.type === 'remove-entity') {
            if (onChart.size === 0) {
                return true;
            }
            const ref = normalizeMarkupEntityRef(op.ref);
            return !ref || onChart.has(ref);
        }
        if (op?.type === 'remove-arrow') {
            if (!Array.isArray(chartConnections)) {
                return true;
            }
            return connectionExists(chartConnections, op.from, op.to);
        }
        return true;
    });
}

/**
 * Drop add-arrow ops whose endpoints are not on the chart and not accepted add-entity refs.
 * A pending add-entity is accepted until it fails or is skipped; after that, leftover
 * Connect requires an on-chart card or a successful add in the same batch.
 * @param {Array<object>} ops
 * @param {Array<object>|null|undefined} chartEntities
 * @param {{opStatuses?: Record<string, string>|null}} [options]
 * @returns {Array<object>}
 */
export function filterInvalidAddArrowOps(ops, chartEntities, options = {}) {
    if (!Array.isArray(ops) || ops.length === 0) {
        return [];
    }
    const valid = new Set([
        ...collectOnChartRefs(chartEntities),
        ...collectAcceptedAddRefs(ops, options?.opStatuses),
    ]);
    if (valid.size === 0) {
        return ops.filter((op) => op?.type !== 'add-arrow' || keepResolvedMarkupOp(op, options.opStatuses));
    }
    return ops.filter((op) => {
        if (keepResolvedMarkupOp(op, options.opStatuses)) {
            return true;
        }
        if (op?.type !== 'add-arrow') {
            return true;
        }
        const fromRef = normalizeMarkupEntityRef(op.from);
        const toRef = normalizeMarkupEntityRef(op.to);
        if (isSelfReferentialArrow(fromRef, toRef)) {
            return false;
        }
        return fromRef && toRef && valid.has(fromRef) && valid.has(toRef);
    });
}

/**
 * Drop duplicate add-arrow ops (on-chart edges or repeated from→to in the batch).
 * @param {Array<object>} ops
 * @param {Array<object>|null|undefined} chartEntities
 * @param {Array<object>|null|undefined} chartConnections
 * @returns {Array<object>}
 */
export function filterDuplicateAddArrowOps(ops, chartEntities, chartConnections, options = {}) {
    if (!Array.isArray(ops) || ops.length === 0) {
        return [];
    }
    const existing = new Set();
    for (const connection of chartConnections || []) {
        const key = arrowEndpointKey(connection?.from, connection?.to);
        if (key) {
            existing.add(key);
        }
    }
    const seenInBatch = new Set();
    return ops.filter((op) => {
        if (keepResolvedMarkupOp(op, options.opStatuses)) {
            return true;
        }
        if (op?.type !== 'add-arrow') {
            return true;
        }
        const key = arrowEndpointKey(op.from, op.to);
        if (!key || existing.has(key) || seenInBatch.has(key)) {
            return false;
        }
        seenInBatch.add(key);
        return true;
    });
}

/**
 * Drop duplicate add-entity/create-local ops in the same batch (same ref or label).
 * @param {Array<object>} ops
 * @returns {Array<object>}
 */
export function filterDuplicateAddEntityOps(ops, options = {}) {
    if (!Array.isArray(ops) || ops.length === 0) {
        return [];
    }
    const seen = new Set();
    return ops.filter((op) => {
        if (keepResolvedMarkupOp(op, options.opStatuses)) {
            return true;
        }
        if (op?.type !== 'add-entity' && op?.type !== 'create-local') {
            return true;
        }
        const key = addEntityDedupeKey(op);
        if (!key) {
            return true;
        }
        if (seen.has(key)) {
            return false;
        }
        seen.add(key);
        return true;
    });
}

function addEntityDedupeKey(op) {
    const ref = normalizeMarkupEntityRef(op.ref);
    if (ref) {
        return `ref:${ref}`;
    }
    const label = op.label != null ? String(op.label).trim().toLowerCase() : '';
    if (label) {
        return `label:${label}`;
    }
    return '';
}

/**
 * Server-aligned markup preview filters for entity adds and connection suggestions.
 * @param {Array<object>} ops
 * @param {Array<object>|null|undefined} chartEntities
 * @param {Array<object>|null|undefined} chartConnections
 * @param {{opStatuses?: Record<string, string>|null}} [options]
 * @returns {Array<object>}
 */
export function filterMarkupPreviewOps(ops, chartEntities, chartConnections, options = {}) {
    const coalesced = coalesceRemoveAddLabelChanges(ops);
    const withoutOnChartAdds = filterAlreadyOnChartOps(coalesced, chartEntities, options);
    const withoutDuplicateAdds = filterDuplicateAddEntityOps(withoutOnChartAdds, options);
    const withoutGoneRemoves = options.filterSatisfiedRemoves === false
        ? withoutDuplicateAdds
        : filterAlreadyGoneRemoveOps(withoutDuplicateAdds, chartEntities, chartConnections, options);
    const withValidArrows = filterInvalidAddArrowOps(withoutGoneRemoves, chartEntities, options);
    return filterDuplicateAddArrowOps(withValidArrows, chartEntities, chartConnections, options);
}

/**
 * Same-pair remove-arrow + add-arrow with a new label is a label edit.
 * Coalesce to one update-arrow so the preview cannot hide the add (duplicate
 * while the edge still exists) and then reveal it after the remove is applied.
 * @param {Array<object>} ops
 * @returns {Array<object>}
 */
export function coalesceRemoveAddLabelChanges(ops) {
    if (!Array.isArray(ops) || ops.length === 0) {
        return Array.isArray(ops) ? ops : [];
    }
    const firstRemove = new Map();
    const firstAdd = new Map();
    ops.forEach((op, index) => {
        const type = normalizeMarkupArrowType(op?.type);
        const pair = undirectedMarkupPairKey(op?.from, op?.to);
        if (!pair) {
            return;
        }
        if (type === 'remove-arrow' && !firstRemove.has(pair)) {
            firstRemove.set(pair, index);
        }
        if (type === 'add-arrow' && !firstAdd.has(pair)) {
            firstAdd.set(pair, index);
        }
    });
    const drop = new Set();
    const replacements = new Map();
    for (const [pair, removeIdx] of firstRemove.entries()) {
        const addIdx = firstAdd.get(pair);
        if (addIdx == null) {
            continue;
        }
        const removeOp = ops[removeIdx];
        const addOp = ops[addIdx];
        const newLabel = addOp?.label != null ? String(addOp.label).trim() : '';
        if (!newLabel) {
            continue;
        }
        const oldLabel = removeOp?.label != null ? String(removeOp.label).trim() : '';
        if (oldLabel && oldLabel.toLowerCase() === newLabel.toLowerCase()) {
            continue;
        }
        replacements.set(removeIdx, {
            type: 'update-arrow',
            from: removeOp.from,
            to: removeOp.to,
            label: newLabel,
        });
        drop.add(addIdx);
        ops.forEach((op, index) => {
            if (index === removeIdx || index === addIdx) {
                return;
            }
            const type = normalizeMarkupArrowType(op?.type);
            if ((type === 'remove-arrow' || type === 'add-arrow')
                && undirectedMarkupPairKey(op?.from, op?.to) === pair) {
                drop.add(index);
            }
        });
    }
    if (replacements.size === 0) {
        return ops;
    }
    return ops
        .map((op, index) => (drop.has(index) ? null : (replacements.get(index) || op)))
        .filter(Boolean);
}

function normalizeMarkupArrowType(type) {
    const normalized = String(type || '').trim().toLowerCase().replace(/_/g, '-');
    if (normalized === 'delete-arrow') {
        return 'remove-arrow';
    }
    return normalized;
}

function undirectedMarkupPairKey(from, to) {
    const fromRef = normalizeMarkupEntityRef(from);
    const toRef = normalizeMarkupEntityRef(to);
    if (!fromRef || !toRef) {
        return '';
    }
    return fromRef < toRef ? `${fromRef}|${toRef}` : `${toRef}|${fromRef}`;
}

/**
 * @param {string|null|undefined} markup
 * @param {Array<object>|null|undefined} ops
 * @param {Array<string>|null|undefined} warnings
 * @param {Array<object>|null|undefined} chartEntities
 * @returns {{ops: Array<object>, warnings: string[], displayMarkdown: string}}
 */
export function resolveMarkupPreviewInput({
    markup,
    ops,
    warnings,
    chartEntities,
    chartConnections,
    opStatuses,
    inferFromChart = false,
} = {}) {
    const parsed = Array.isArray(ops) && ops.length > 0
        ? {ops, warnings: [], displayMarkdown: ''}
        : parseChartMutationMarkup(typeof markup === 'string' ? markup : '');
    const rawOps = parsed.ops;
    const inferredOpStatuses = inferFromChart
        ? inferMarkupOpStatusesFromChart(rawOps, chartEntities, chartConnections, opStatuses)
        : (opStatuses && typeof opStatuses === 'object' ? opStatuses : {});
    const filterOps = (list) => filterMarkupPreviewOps(list, chartEntities, chartConnections, {
        opStatuses: inferredOpStatuses,
        filterSatisfiedRemoves: !inferFromChart,
    });
    if (Array.isArray(ops) && ops.length > 0) {
        return {
            ops: filterOps(ops),
            warnings: Array.isArray(warnings) ? warnings : [],
            displayMarkdown: '',
            inferredOpStatuses,
        };
    }
    return {
        ops: filterOps(parsed.ops),
        warnings: [
            ...(Array.isArray(warnings) ? warnings : []),
            ...parsed.warnings,
        ],
        displayMarkdown: parsed.displayMarkdown,
        inferredOpStatuses,
    };
}

function SkippedStatus() {
    return (
        <Stack direction="row" spacing={0.5} alignItems="center" sx={{color: 'warning.main'}}>
            <RemoveCircleOutlineIcon data-testid="pure-llm-op-skipped-icon" sx={{fontSize: 16}}/>
            <Typography variant="caption" sx={{fontWeight: 600, color: 'warning.main'}}>
                Skipped
            </Typography>
        </Stack>
    );
}

function MarkupOpRow({
    op,
    labelIndex,
    chartReady,
    status,
    errorMessage,
    frozen = false,
    imageUrl = '',
    fromImageUrl = '',
    toImageUrl = '',
    onApprove,
    onReject,
}) {
    const isBusy = status === 'applying';
    const description = describeMarkupOp(op, labelIndex);
    const actionVerb = markupOpActionVerb(op);
    const appliedLabel = markupOpAppliedLabel(op);
    const isArrow = isArrowMarkupOp(op);
    const showActions = !frozen
        && status !== 'applied'
        && status !== 'rejected'
        && !(status === 'error' && isHiddenPureLlmApplyError(errorMessage));

    return (
        <Box
            data-testid={`pure-llm-op-row-${markupOpKey(op)}`}
            sx={{
                display: 'flex',
                flexDirection: 'column',
                gap: 0.75,
                px: 1,
                py: 0.75,
                borderRadius: 1,
                bgcolor: 'grey.50',
                border: '1px solid',
                borderColor: 'divider',
            }}
        >
            <Stack direction="row" spacing={0.75} alignItems="center">
                {isArrow ? (
                    <Stack direction="row" spacing={0.25} alignItems="center" sx={{flexShrink: 0}}>
                        <MarkupEntityAvatar
                            src={fromImageUrl}
                            label={displayRef(op.from, labelIndex)}
                            testId={`pure-llm-op-entity-image-${normalizeMarkupEntityRef(op.from)}`}
                            size={24}
                        />
                        <Typography variant="caption" sx={{fontSize: 11, color: 'text.secondary', px: 0.15}}>
                            →
                        </Typography>
                        <MarkupEntityAvatar
                            src={toImageUrl}
                            label={displayRef(op.to, labelIndex)}
                            testId={`pure-llm-op-entity-image-${normalizeMarkupEntityRef(op.to)}`}
                            size={24}
                        />
                    </Stack>
                ) : (
                    <MarkupEntityAvatar
                        src={imageUrl}
                        label={displayName(op.label, op.ref, displayRef(op.ref, labelIndex))}
                        testId={`pure-llm-op-entity-image-${normalizeMarkupEntityRef(op.ref) || markupOpKey(op)}`}
                        size={28}
                    />
                )}
                <Typography variant="body2" sx={{fontSize: 12, fontWeight: 500, wordBreak: 'break-word'}}>
                    {description}
                </Typography>
            </Stack>

            {status === 'applied' ? (
                <Stack direction="row" spacing={0.5} alignItems="center" sx={{color: 'success.main'}}>
                    <CheckCircleOutlineIcon sx={{fontSize: 16}}/>
                    <Typography variant="caption" sx={{fontWeight: 600}}>{appliedLabel}</Typography>
                </Stack>
            ) : status === 'rejected' ? (
                <SkippedStatus/>
            ) : status === 'error' && !isHiddenPureLlmApplyError(errorMessage) ? (
                <Stack direction="row" spacing={0.5} alignItems="center" flexWrap="wrap" useFlexGap>
                    <Typography variant="caption" color="error" sx={{wordBreak: 'break-word'}}>
                        {previewApplyErrorText(errorMessage)}
                    </Typography>
                    {!frozen ? (
                        <Button size="small" onClick={onApprove} disabled={!chartReady || isBusy}>
                            Retry
                        </Button>
                    ) : null}
                </Stack>
            ) : showActions ? (
                <Stack direction="row" spacing={0.5} justifyContent="flex-end" sx={{alignSelf: 'flex-end'}}>
                    <Button
                        size="small"
                        variant="outlined"
                        color="inherit"
                        onClick={onReject}
                        disabled={isBusy}
                        aria-label={`Skip ${actionVerb} ${description}`}
                        sx={COMPACT_ACTION_SX}
                    >
                        Skip
                    </Button>
                    <Button
                        size="small"
                        variant="contained"
                        onClick={onApprove}
                        disabled={!chartReady || isBusy}
                        aria-label={`${actionVerb} ${description}`}
                        startIcon={isBusy ? <CircularProgress size={10} color="inherit"/> : null}
                        sx={COMPACT_ACTION_SX}
                    >
                        {actionVerb}
                    </Button>
                </Stack>
            ) : null}
        </Box>
    );
}

/**
 * Preview + per-op confirm UI for Pure LLM chart markup.
 * Content ops (add/remove/update) need confirmation; move-entity is silent and
 * is bundled automatically on Add all / batch apply (not on one-by-one Add).
 *
 * @param {{
 *   markup?: string,
 *   ops?: Array<object>,
 *   warnings?: Array<string>,
 *   chartId?: string,
 *   summary?: string,
 *   frozen?: boolean,
 *   initialOpStatuses?: Record<string, string>,
 *   chartApplyGeneration?: number,
 *   suppressParseWarnings?: boolean,
 *   onReject: () => void,
 *   onFrozen?: (detail: {opStatuses: Record<string, string>}) => void,
 *   onApplied?: (result: object) => void,
 * }} props
 */
const PureLlmMutationPreview = ({
    markup,
    ops: opsProp,
    warnings: warningsProp,
    chartId,
    summary,
    frozen: frozenProp = false,
    initialOpStatuses = null,
    chartApplyGeneration = 0,
    suppressParseWarnings = false,
    onReject,
    onFrozen,
    onApplied,
}) => {
    const canApplyMarkupForChart = useChartAiBridgeStore((s) => s.canApplyMarkup);
    const applyMarkupMutations = useChartAiBridgeStore((s) => s.applyMarkupMutations);
    const chartEntities = useChartAiBridgeStore((s) => s.chartEntities);
    const chartConnections = useChartAiBridgeStore((s) => s.chartConnections);

    const [opStatuses, setOpStatuses] = useState(() => (
        initialOpStatuses && typeof initialOpStatuses === 'object' ? {...initialOpStatuses} : {}
    ));
    const [opErrors, setOpErrors] = useState({});
    const [frozen, setFrozen] = useState(Boolean(frozenProp));
    const initialOpStatusesRef = useRef(initialOpStatuses);
    initialOpStatusesRef.current = initialOpStatuses;
    const frozenPropRef = useRef(frozenProp);
    frozenPropRef.current = frozenProp;
    const frozenOpKeysRef = useRef(new Set());
    const didUserFreezeRef = useRef(false);
    const autoApplyInFlightRef = useRef(false);
    const loggedWarningsRef = useRef(new Set());

    // Reset preview local state only on a new turn — enrichment must keep statuses.
    useEffect(() => {
        const seed = initialOpStatusesRef.current;
        setOpStatuses(seed && typeof seed === 'object' ? {...seed} : {});
        setOpErrors({});
        setFrozen(Boolean(frozenPropRef.current));
        frozenOpKeysRef.current = new Set();
        didUserFreezeRef.current = false;
        autoApplyInFlightRef.current = false;
        loggedWarningsRef.current = new Set();
    }, [chartApplyGeneration]);

    useEffect(() => {
        setFrozen(Boolean(frozenProp));
    }, [frozenProp]);

    const chartReady = canApplyMarkupForChart(chartId);
    const chartSnapshotReady = (chartEntities || []).length > 0
        || (chartConnections || []).length > 0
        || Boolean(initialOpStatuses && Object.keys(initialOpStatuses).length);
    // Wait for a chart snapshot so an empty reload cannot mark every remove as applied.
    const inferFromChart = chartReady
        && chartSnapshotReady
        && (frozen || frozenProp || chartApplyGeneration <= 0);

    const chartEntityKey = useMemo(() => chartEntityIdentityKey(chartEntities), [chartEntities]);
    const chartConnectionKey = useMemo(
        () => chartConnectionIdentityKey(chartConnections),
        [chartConnections]
    );
    const resolved = useMemo(
        () => resolveMarkupPreviewInput({
            markup,
            ops: opsProp,
            warnings: warningsProp,
            chartEntities,
            chartConnections,
            opStatuses,
            inferFromChart,
        }),
        // Identity keys ignore card x/y so arrange/settle ticks do not re-parse markup.
        // eslint-disable-next-line react-hooks/exhaustive-deps
        [markup, opsProp, warningsProp, chartEntityKey, chartConnectionKey, inferFromChart, opStatuses]
    );
    const ops = resolved.ops;
    const warnings = resolved.warnings;
    const displayStatuses = useMemo(
        () => ({...(resolved.inferredOpStatuses || {}), ...opStatuses}),
        [opStatuses, resolved.inferredOpStatuses]
    );
    useEffect(() => {
        if (suppressParseWarnings || !warnings.length) {
            return;
        }
        // `markup` grows on every streaming chunk while the agentic run is in
        // flight, so `resolved` (and this `warnings` array) is recomputed —
        // with a fresh array identity — many times per turn. Without
        // dedup, the same "chart-op #N: ..." warning gets logged again on
        // every intermediate chunk, producing an unbounded stream of
        // duplicate console warnings for a single AI response.
        const alreadyLogged = loggedWarningsRef.current;
        for (const warning of warnings) {
            if (alreadyLogged.has(warning)) {
                continue;
            }
            alreadyLogged.add(warning);
            console.warn('[chart-ai]', warning);
        }
    }, [warnings, suppressParseWarnings]);

    const labelIndex = useMemo(() => buildMarkupLabelIndex(ops, chartEntities), [ops, chartEntities]);

    const opEntries = useMemo(
        () => ops.map((op, index) => ({op, index, key: markupOpKey(op, index)})),
        [ops]
    );

    const confirmableEntries = useMemo(
        () => opEntries.filter(({op}) => isConfirmableMarkupOp(op)),
        [opEntries]
    );

    const pendingConfirmableEntries = useMemo(
        () => confirmableEntries.filter(({key}) => {
            const status = displayStatuses[key];
            return status !== 'applied' && status !== 'rejected';
        }),
        [confirmableEntries, displayStatuses]
    );

    const pendingSilentMoveEntries = useMemo(
        () => opEntries.filter(({op, key}) => {
            if (!isSilentMarkupOp(op)) return false;
            const status = displayStatuses[key];
            return status !== 'applied' && status !== 'rejected';
        }),
        [displayStatuses, opEntries]
    );

    const isApplyingAny = useMemo(
        () => Object.values(displayStatuses).some((status) => status === 'applying'),
        [displayStatuses]
    );

    const allConfirmableResolved = confirmableEntries.length > 0
        && pendingConfirmableEntries.length === 0
        && !isApplyingAny;

    const freezePreview = useCallback((nextStatuses) => {
        const statuses = nextStatuses || displayStatuses;
        didUserFreezeRef.current = true;
        confirmableEntries.forEach(({key}) => {
            frozenOpKeysRef.current.add(key);
        });
        setFrozen(true);
        onFrozen?.({opStatuses: statuses});
    }, [confirmableEntries, displayStatuses, onFrozen]);

    const lastFrozenStatusesRef = useRef(null);
    useEffect(() => {
        if (frozen || frozenProp) {
            if (allConfirmableResolved
                && !markupOpStatusesEqual(lastFrozenStatusesRef.current, displayStatuses)) {
                lastFrozenStatusesRef.current = displayStatuses;
                onFrozen?.({opStatuses: displayStatuses});
            }
            return;
        }
        if (allConfirmableResolved) {
            lastFrozenStatusesRef.current = displayStatuses;
            freezePreview(displayStatuses);
        }
    }, [allConfirmableResolved, displayStatuses, freezePreview, frozen, frozenProp, onFrozen]);

    /** Content batch + any still-pending move-entity ops (no per-row approval). */
    const withSilentMoves = useCallback((entries) => {
        const keys = new Set(entries.map(({key}) => key));
        const moves = pendingSilentMoveEntries.filter(({key}) => !keys.has(key));
        return [...entries, ...moves];
    }, [pendingSilentMoveEntries]);

    const applyOpBatch = useCallback(async (entries, {runHubSquare = false} = {}) => {
        if (!chartReady || typeof applyMarkupMutations !== 'function') {
            throw new Error('Open chart to apply changes');
        }
        if (entries.length === 0) {
            return null;
        }
        setOpStatuses((current) => {
            const next = {...current};
            entries.forEach(({key}) => {
                next[key] = 'applying';
            });
            return next;
        });
        setOpErrors((current) => {
            const next = {...current};
            entries.forEach(({key}) => {
                delete next[key];
            });
            return next;
        });
        try {
            const result = await applyMarkupMutations({
                chartId,
                ops: entries.map(({op}) => op),
                runHubSquare,
            });
            onApplied?.(result);
            const hardFails = (result?.failed || []).filter(
                (f) => !(f?.soft || isSoftArrowEndpointError(f?.error))
            );
            const softFails = (result?.failed || []).filter(
                (f) => f?.soft || isSoftArrowEndpointError(f?.error)
            );
            if (hardFails.length || softFails.length) {
                const nextStatuses = {};
                const nextErrors = {};
                for (const failure of [...hardFails, ...softFails]) {
                    const failedIndex = entries.findIndex(({op}) => op === failure?.op);
                    const entry = failedIndex >= 0 ? entries[failedIndex] : entries[0];
                    const soft = failure?.soft || isSoftArrowEndpointError(failure?.error);
                    nextStatuses[entry.key] = soft && (result?.applied || 0) > 0 ? 'applied' : 'error';
                    if (!soft || (result?.applied || 0) === 0) {
                        nextErrors[entry.key] = formatChartErrorForUser(failure?.error);
                    }
                }
                for (const entry of entries) {
                    if (nextStatuses[entry.key]) continue;
                    nextStatuses[entry.key] = 'applied';
                }
                setOpStatuses((current) => ({...current, ...nextStatuses}));
                setOpErrors((current) => ({...current, ...nextErrors}));
                const summary = summarizeMarkupApplyResult(result);
                if (!summary.ok) {
                    throw new Error(summary.message || formatChartErrorForUser(hardFails[0]?.error));
                }
                return result;
            }
            const appliedStatuses = {};
            for (const entry of entries) {
                appliedStatuses[entry.key] = 'applied';
            }
            setOpStatuses((current) => ({...current, ...appliedStatuses}));
            return result;
        } catch (error) {
            const message = error?.message || 'Failed to apply chart change';
            if (isHiddenPureLlmApplyError(message)) {
                setOpStatuses((current) => {
                    const next = {...current};
                    entries.forEach(({key}) => {
                        if (next[key] === 'applying') {
                            delete next[key];
                        }
                    });
                    return next;
                });
                throw error;
            }
            setOpStatuses((current) => {
                const next = {...current};
                entries.forEach(({key}) => {
                    if (next[key] === 'applying') {
                        next[key] = 'error';
                    }
                });
                return next;
            });
            setOpErrors((current) => {
                const next = {...current};
                entries.forEach(({key}) => {
                    if (next[key] === 'applying' || !next[key]) {
                        next[key] = message;
                    }
                });
                return next;
            });
            throw error;
        }
    }, [applyMarkupMutations, chartId, chartReady, onApplied]);

    // Same-turn enrichment after Add all: auto-apply the leftover delta
    // (entities before arrows). History / inactive previews (generation 0) stay still.
    useEffect(() => {
        if (chartApplyGeneration <= 0 || (!frozen && !frozenProp)) {
            return undefined;
        }
        if (autoApplyInFlightRef.current || isApplyingAny) {
            return undefined;
        }
        const leftover = confirmableEntries.filter(({key}) => {
            const status = displayStatuses[key];
            return status !== 'applied'
                && status !== 'rejected'
                && status !== 'error'
                && status !== 'applying';
        });
        if (leftover.length === 0) {
            return undefined;
        }
        autoApplyInFlightRef.current = true;
        leftover.forEach(({key}) => {
            frozenOpKeysRef.current.add(key);
        });
        void applyOpBatch(
            withSilentMoves(orderEntriesEntitiesFirst(leftover)),
            {runHubSquare: false}
        ).catch((error) => {
            console.error('[PureLlmMutationPreview] Auto-apply leftover failed', error);
        }).finally(() => {
            autoApplyInFlightRef.current = false;
        });
        return undefined;
    }, [
        applyOpBatch,
        chartApplyGeneration,
        confirmableEntries,
        frozen,
        frozenProp,
        isApplyingAny,
        displayStatuses,
        withSilentMoves,
    ]);

    const handleApprove = useCallback(async (op, index) => {
        const key = markupOpKey(op, index);
        const status = displayStatuses[key];
        // Frozen leftover Connect must still apply — not no-op-then-Skipped.
        if (frozen && (status === 'applied' || status === 'rejected')) {
            return;
        }
        try {
            // One-by-one: place/nudge only — do not bundle silent moves or hub-square.
            await applyOpBatch([{op, index, key}], {runHubSquare: false});
        } catch (error) {
            if (isHiddenPureLlmApplyError(error?.message)) {
                return;
            }
            // Status already set in applyOpBatch
        }
    }, [applyOpBatch, displayStatuses, frozen]);

    const handleReject = useCallback((op, index) => {
        if (frozen) return;
        const key = markupOpKey(op, index);
        setOpStatuses((current) => ({...current, [key]: 'rejected'}));
    }, [frozen]);

    const handleAddAll = useCallback(async () => {
        if (frozen) return;
        try {
            await applyOpBatch(
                withSilentMoves(orderEntriesEntitiesFirst(pendingConfirmableEntries)),
                {runHubSquare: false}
            );
        } catch (error) {
            console.error('[PureLlmMutationPreview] Add all failed', error);
        }
    }, [applyOpBatch, frozen, pendingConfirmableEntries, withSilentMoves]);

    const handleDismissAll = useCallback(() => {
        if (frozen) return;
        const rejectedStatuses = {};
        for (const {key} of pendingConfirmableEntries) {
            rejectedStatuses[key] = 'rejected';
        }
        // Dismiss content without applying — also drop silent moves so they are not left pending.
        for (const {key} of pendingSilentMoveEntries) {
            rejectedStatuses[key] = 'rejected';
        }
        setOpStatuses((current) => {
            const next = {...current, ...rejectedStatuses};
            freezePreview(next);
            return next;
        });
    }, [freezePreview, frozen, pendingConfirmableEntries, pendingSilentMoveEntries]);

    // Moves-only markup is handled by the parent as mutationMode=auto; never show an empty list.
    if (isMovesOnlyMarkupOps(ops)) {
        return null;
    }

    const hasProposedMarkup = (typeof markup === 'string' && markup.trim().length > 0)
        || (Array.isArray(opsProp) && opsProp.length > 0);

    if (!ops.length || !confirmableEntries.length) {
        if (!hasProposedMarkup) {
            return null;
        }
        return (
            <Box
                data-testid="pure-llm-mutation-preview"
                data-empty="true"
                sx={{
                    border: '1px solid',
                    borderColor: 'divider',
                    borderRadius: 1,
                    p: 1.5,
                    bgcolor: 'background.default',
                }}
            >
                <Stack spacing={1}>
                    <Stack direction="row" spacing={0.75} alignItems="center">
                        <AccountTreeOutlinedIcon sx={{fontSize: 16, color: 'text.secondary'}}/>
                        <Typography variant="subtitle2" sx={{fontWeight: 600, fontSize: 13}}>
                            Suggested chart changes
                        </Typography>
                    </Stack>
                    <Typography
                        variant="body2"
                        color="text.secondary"
                        data-testid="pure-llm-mutation-empty"
                        sx={{fontSize: 12}}
                    >
                        {CHART_PREVIEW_EMPTY_COPY}
                    </Typography>
                </Stack>
            </Box>
        );
    }

    const isFrozen = frozen || frozenProp;
    const showBulkActions = !isFrozen && pendingConfirmableEntries.length >= 2;
    const bulkApplyLabel = confirmableEntries.some(({op}) => isRemoveMarkupOp(op))
        ? 'Apply all'
        : 'Add all';

    return (
        <Box
            data-testid="pure-llm-mutation-preview"
            data-frozen={isFrozen ? 'true' : 'false'}
            sx={{
                border: '1px solid',
                borderColor: 'divider',
                borderRadius: 1,
                p: 1.5,
                bgcolor: 'background.default',
                opacity: isFrozen ? 0.85 : 1,
            }}
        >
            <Stack spacing={1}>
                <Stack direction="row" spacing={0.75} alignItems="center">
                    <AccountTreeOutlinedIcon sx={{fontSize: 16, color: 'text.secondary'}}/>
                    <Typography variant="subtitle2" sx={{fontWeight: 600, fontSize: 13}}>
                        Suggested chart changes
                    </Typography>
                </Stack>

                {isAnalystVisibleChartPlanSummary(summary) ? (
                    <Typography variant="body2" color="text.secondary" sx={{fontSize: 12}}>
                        {summary}
                    </Typography>
                ) : null}

                {confirmableEntries.length > 0 ? (
                    <Stack spacing={0.75}>
                        {confirmableEntries.map(({op, index, key}) => {
                            const {imageUrl, fromUrl, toUrl} = resolveMarkupOpImages(op, chartEntities);
                            return (
                            <MarkupOpRow
                                key={key}
                                op={op}
                                labelIndex={labelIndex}
                                chartReady={chartReady}
                                status={
                                    displayStatuses[key]
                                    || (isFrozen && chartApplyGeneration <= 0 ? 'rejected' : null)
                                }
                                errorMessage={opErrors[key]}
                                frozen={isFrozen}
                                imageUrl={imageUrl}
                                fromImageUrl={fromUrl}
                                toImageUrl={toUrl}
                                onApprove={() => handleApprove(op, index)}
                                onReject={() => handleReject(op, index)}
                            />
                            );
                        })}
                    </Stack>
                ) : (
                    <Typography variant="caption" color="text.secondary">
                        {CHART_PREVIEW_EMPTY_COPY}
                    </Typography>
                )}

                {showBulkActions ? (
                    <Stack
                        direction="row"
                        justifyContent="center"
                        spacing={2}
                        sx={{pt: 2.5, mt: 0.5}}
                    >
                        <Button
                            size="small"
                            color="inherit"
                            variant="outlined"
                            onClick={handleDismissAll}
                            disabled={isApplyingAny}
                            aria-label="Dismiss all chart changes"
                            sx={{minWidth: 0, px: 1.25}}
                        >
                            Dismiss All
                        </Button>
                        <Button
                            size="small"
                            variant="contained"
                            onClick={handleAddAll}
                            disabled={!chartReady || isApplyingAny || pendingConfirmableEntries.length === 0}
                            aria-label={`${bulkApplyLabel} chart changes`}
                            startIcon={isApplyingAny ? <CircularProgress size={12} color="inherit"/> : null}
                            sx={{minWidth: 0, px: 1.25}}
                        >
                            {bulkApplyLabel}
                        </Button>
                    </Stack>
                ) : null}
            </Stack>
        </Box>
    );
};

export default PureLlmMutationPreview;
